// src/components/coordinateGrid/GradientTriangleRenderer.tsx
/**
 * GradientTriangleRenderer Component
 * 
 * Renders gradient visualization triangles for linear equations.
 * Shows rise/run with labels.
 * 
 * Phase 2: Graphing Equations
 */

import React from 'react';
import { GradientTriangle, Point2D } from '../../types/coordinateGrid';

interface GradientTriangleRendererProps {
  triangle: GradientTriangle;
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
  isDark?: boolean;
}

/**
 * Convert math coordinates to screen coordinates
 */
function mathToScreen(
  point: Point2D,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number
): { x: number; y: number } {
  return {
    x: offsetX + point.x * scaleX,
    y: offsetY - point.y * scaleY, // Y is inverted in screen coordinates
  };
}

export const GradientTriangleRenderer: React.FC<GradientTriangleRendererProps> = ({
  triangle,
  scaleX,
  scaleY,
  offsetX,
  offsetY,
  isDark = false,
}) => {
  const p1Screen = mathToScreen(triangle.point1, scaleX, scaleY, offsetX, offsetY);
  const p2Screen = mathToScreen(triangle.point2, scaleX, scaleY, offsetX, offsetY);

  // Calculate the right-angled triangle
  // We want to show the horizontal "run" and vertical "rise"
  const dx = triangle.point2.x - triangle.point1.x;
  const dy = triangle.point2.y - triangle.point1.y;

  // Create a right-angled triangle
  // Point 1 is on the line
  // Point 2 is on the line
  // Point 3 is the right angle (directly below or above point 1, depending on direction)
  const rightAnglePoint: Point2D = {
    x: triangle.point1.x,
    y: triangle.point2.y, // Same y as point 2, same x as point 1
  };
  const p3Screen = mathToScreen(rightAnglePoint, scaleX, scaleY, offsetX, offsetY);

  // Calculate gradient
  const gradient = dx !== 0 ? dy / dx : (dy > 0 ? Infinity : -Infinity);

  // Calculate distances for labels
  const run = Math.abs(dx);
  const rise = Math.abs(dy);

  // Determine label positions
  const midX = (p1Screen.x + p3Screen.x) / 2;
  const midY = (p1Screen.y + p3Screen.y) / 2;
  const runLabelX = (p1Screen.x + p2Screen.x) / 2;
  const runLabelY = Math.max(p1Screen.y, p2Screen.y) + 15;
  const riseLabelX = Math.max(p1Screen.x, p2Screen.x) + 15;
  const riseLabelY = (p1Screen.y + p2Screen.y) / 2;

  return (
    <g className={`gradient-triangle-${triangle.id}`}>
      {/* Triangle outline */}
      <polygon
        points={`${p1Screen.x},${p1Screen.y} ${p2Screen.x},${p2Screen.y} ${p3Screen.x},${p3Screen.y}`}
        fill={triangle.color}
        fillOpacity={0.2}
        stroke={triangle.color}
        strokeWidth={2}
        strokeDasharray="4,4"
      />

      {/* Horizontal line (run) */}
      <line
        x1={p1Screen.x}
        y1={p3Screen.y}
        x2={p2Screen.x}
        y2={p3Screen.y}
        stroke={triangle.color}
        strokeWidth={2}
        strokeDasharray="4,4"
      />

      {/* Vertical line (rise) */}
      <line
        x1={p3Screen.x}
        y1={p3Screen.y}
        x2={p2Screen.x}
        y2={p2Screen.y}
        stroke={triangle.color}
        strokeWidth={2}
        strokeDasharray="4,4"
      />

      {/* Labels */}
      {triangle.showLabels && (
        <>
          {/* Run label (Δx) */}
          <text
            x={runLabelX}
            y={runLabelY}
            fontSize="12"
            fill={isDark ? '#fff' : '#000'}
            textAnchor="middle"
            style={{
              textShadow: isDark 
                ? '1px 1px 2px rgba(0,0,0,0.8)' 
                : '1px 1px 2px rgba(255,255,255,0.8)',
            }}
          >
            Δx = {run.toFixed(2)}
          </text>

          {/* Rise label (Δy) */}
          <text
            x={riseLabelX}
            y={riseLabelY}
            fontSize="12"
            fill={isDark ? '#fff' : '#000'}
            textAnchor="start"
            style={{
              textShadow: isDark 
                ? '1px 1px 2px rgba(0,0,0,0.8)' 
                : '1px 1px 2px rgba(255,255,255,0.8)',
            }}
          >
            Δy = {rise.toFixed(2)}
          </text>

          {/* Gradient value */}
          <text
            x={midX}
            y={midY - 10}
            fontSize="14"
            fill={triangle.color}
            fontWeight="600"
            textAnchor="middle"
            style={{
              textShadow: isDark 
                ? '1px 1px 2px rgba(0,0,0,0.8)' 
                : '1px 1px 2px rgba(255,255,255,0.8)',
            }}
          >
            m = {gradient.toFixed(2)}
          </text>
        </>
      )}
    </g>
  );
};



